import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import { sendBondCalculation } from '../http/api';

export const useBondCalculationStore = create((set, get) => ({
  // Map to store calculation results indexed by UUID
  calculationResults: {},
  
  // Map to track pending calculation requests: { uuid: { cusip, price, timestamp, rungIndex, originalValues } }
  pendingRequests: {},
  
  // Map to track which rungs have timed out requests: { rungIndex: true }
  timeoutMessages: {},
  
  // Add a new calculation request
  addPendingRequest: (uuid, rungIndex, cusip, price, originalValues = null) => set((state) => {
    console.log(`Adding pending request ${uuid} for rung ${rungIndex}`);
    return {
      pendingRequests: {
        ...state.pendingRequests,
        [uuid]: {
          cusip,
          price,
          timestamp: Date.now(),
          rungIndex,
          originalValues
        }
      }
    };
  }),
  
  // Remove a pending request
  removePendingRequest: (uuid) => set((state) => {
    const newPendingRequests = { ...state.pendingRequests };
    delete newPendingRequests[uuid];
    return { pendingRequests: newPendingRequests };
  }),
  
  // Store calculation result
  storeCalculationResult: (uuid, result) => set((state) => {
    // Get the rung index associated with this UUID
    const request = state.pendingRequests[uuid];
    if (!request) return state;
    
    // Remove timeout message for this rung if exists
    const newTimeoutMessages = { ...state.timeoutMessages };
    if (newTimeoutMessages[request.rungIndex]) {
      delete newTimeoutMessages[request.rungIndex];
    }
    
    return {
      calculationResults: {
        ...state.calculationResults,
        [uuid]: result
      },
      timeoutMessages: newTimeoutMessages
    };
  }),
  
  // Mark a request as timed out
  markRequestTimedOut: (uuid) => set((state) => {
    const request = state.pendingRequests[uuid];
    if (!request) return state;
    
    // Remove the request from pending
    const newPendingRequests = { ...state.pendingRequests };
    delete newPendingRequests[uuid];
    
    // Mark the rung as having a timeout
    return {
      pendingRequests: newPendingRequests,
      timeoutMessages: {
        ...state.timeoutMessages,
        [request.rungIndex]: true
      }
    };
  }),
  
  // Clear all timeout messages
  clearTimeoutMessage: (rungIndex) => set((state) => {
    const newTimeoutMessages = { ...state.timeoutMessages };
    delete newTimeoutMessages[rungIndex];
    return { timeoutMessages: newTimeoutMessages };
  }),
  
  // Clear all timeout messages
  clearAllTimeoutMessages: () => set({ timeoutMessages: {} }),
  
  // Get all UUIDs of pending requests
  getPendingUUIDs: () => Object.keys(get().pendingRequests),
  
  // Get calculation by UUID
  getCalculationByUUID: (uuid) => get().calculationResults[uuid],
  
  // Get pending requests by rung index
  getPendingRequestsByRungIndex: () => {
    const result = {};
    Object.entries(get().pendingRequests).forEach(([uuid, request]) => {
      result[request.rungIndex] = { uuid, ...request };
    });
    return result;
  },
  
  // Send a bond calculation request and return the UUID
  calculateBondValues: async (token, userId, cusip, price, rungIndex, originalValues = null) => {
    if (!cusip || cusip.length < 9 || !price) {
      console.log('Skipping calculation - invalid data:', { cusip, price });
      return { success: false, error: "Invalid CUSIP or price", uuid: null };
    }
    
    try {
      const uuid = uuidv4();
      
      // Store the request in our state
      get().addPendingRequest(uuid, rungIndex, cusip, price, originalValues);
      
      const calcData = {
        cusip,
        price: parseFloat(price),
        userId,
        UUID: uuid,
      };
      
      console.log(`Sending calculation request for ${cusip} at ${price} with UUID ${uuid}`);
      const result = await sendBondCalculation(token, calcData);
      
      // Check if the result has an error
      if (result && result.error) {
        console.error("Error from API:", result.message);
        
        // Mark this request as timed out immediately to show error state
        get().markRequestTimedOut(uuid);
        
        return { success: false, error: result.message, uuid: null };
      }
      
      // Return success status along with the UUID
      return { success: true, uuid };
    } catch (error) {
      console.error("Error sending bond calculation:", error);
      return { success: false, error: error.message || "Network error occurred", uuid: null };
    }
  },
  
  // NEW: Send multiple bond calculation requests at once with better error handling
  calculateBondValuesInBatch: async (token, userId, requests) => {
    if (!requests || requests.length === 0) {
      return { success: false, uuids: [], failedCount: 0, error: "No valid requests provided" };
    }
    
    const validRequests = requests.filter(
      req => req.cusip && req.cusip.length >= 9 && req.price
    );
    
    if (validRequests.length === 0) {
      return { success: false, uuids: [], failedCount: 0, error: "No valid requests after filtering" };
    }
    
    console.log(`Attempting batch calculation for ${validRequests.length} bonds`);
    
    // Generate UUIDs and store requests before sending
    const requestsWithUUIDs = validRequests.map(req => {
      const uuid = uuidv4();
      get().addPendingRequest(
        uuid, 
        req.rungIndex, 
        req.cusip, 
        req.price, 
        req.originalValues
      );
      return { ...req, uuid };
    });
    
    let successCount = 0;
    let failedCount = 0;
    const successfulUUIDs = [];
    let lastError = null;
    
    try {
      // Send all requests in parallel, but individually track success/failure
      const results = await Promise.all(requestsWithUUIDs.map(async (req) => {
        try {
          const calcData = {
            cusip: req.cusip,
            price: parseFloat(req.price),
            userId,
            UUID: req.uuid,
          };
          
          const result = await sendBondCalculation(token, calcData);
          
          if (result && result.error) {
            console.error("Error from API for request:", req, result.message);
            get().markRequestTimedOut(req.uuid);
            failedCount++;
            lastError = result.message;
            return { success: false, uuid: req.uuid };
          }
          
          successCount++;
          successfulUUIDs.push(req.uuid);
          return { success: true, uuid: req.uuid };
        } catch (error) {
          console.error("Error sending bond calculation for rung", req.rungIndex, error);
          get().markRequestTimedOut(req.uuid);
          failedCount++;
          lastError = error.message || "Network error";
          return { success: false, uuid: req.uuid };
        }
      }));
      
      // Return comprehensive results
      return { 
        success: successCount > 0,
        uuids: successfulUUIDs,
        successCount,
        failedCount,
        error: failedCount > 0 ? `${failedCount} request(s) failed. Last error: ${lastError}` : null
      };
    } catch (error) {
      console.error("Critical error in batch calculation:", error);
      
      // Mark all as timed out to clean up state
      requestsWithUUIDs.forEach(req => {
        get().markRequestTimedOut(req.uuid);
      });
      
      return { 
        success: false, 
        uuids: [], 
        successCount: 0,
        failedCount: validRequests.length,
        error: error.message || "Failed to send calculation requests" 
      };
    }
  },
  
  // Clear everything
  clearAll: () => set({
    calculationResults: {},
    pendingRequests: {},
    timeoutMessages: {}
  }),
}));
